import React, { useState } from 'react';
import { Factory, Fish, Utensils, Trees, Package, ChevronRight, Star } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getOptimizedUrl, INDUSTRY_IMAGE_MAP, ABOUT_IMAGE_MAP } from '../generated-images-config';

const Industries = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detectar si es móvil
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleIndustryContact = (industryName: string) => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
      // Pre-fill industry in the contact form
      setTimeout(() => {
        const industrySelect = document.querySelector('select[name="industry"]') as HTMLSelectElement;
        if (industrySelect) {
          const industryOptions = {
            'Industria Papelera': 'papelera',
            'Industria Pesquera': 'pesquera',
            'Industria Alimentaria': 'alimentaria',
            'Agroindustria': 'agroindustrial',
            'Industria Manufacturer': 'manufactura'
          };
          
          const industryValue = industryOptions[industryName as keyof typeof industryOptions];
          if (industryValue) {
            industrySelect.value = industryValue;
          }
        }
      }, 500);
    }
  };

  const handleGeneralInquiry = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const industries = [
    {
      id: 0,
      icon: Factory,
      title: 'Industria Papelera',
      description: 'Equipos y servicios especializados para la producción de papel y celulosa.',
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      services: [
        'Mantenimiento de maquinaria pesada',
        'Repuestos para equipos de procesamiento',
        'Instalación de sistemas de control',
        'Optimización de líneas de producción'
      ],
      image: getOptimizedUrl(INDUSTRY_IMAGE_MAP.paper, 600, 400)
    },
    {
      id: 1,
      icon: Fish,
      title: 'Industria Pesquera',
      description: 'Soluciones completas para procesamiento y conservación de productos del mar.',
      color: 'bg-cyan-500',
      bgColor: 'bg-cyan-50',
      services: [
        'Sistemas de refrigeración industrial',
        'Equipos de procesamiento de pescado',
        'Mantenimiento de plantas congeladoras',
        'Sistemas de calidad y control'
      ],
      image: getOptimizedUrl(INDUSTRY_IMAGE_MAP.fishing, 600, 400)
    },
    {
      id: 2,
      icon: Utensils,
      title: 'Industria Alimentaria',
      description: 'Equipos certificados y servicios para la industria de alimentos y bebidas.',
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      services: [
        'Equipos con certificación sanitaria',
        'Sistemas de envasado y empaque',
        'Plantas de procesamiento de alimentos',
        'Control de calidad y trazabilidad'
      ],
      image: getOptimizedUrl(INDUSTRY_IMAGE_MAP.food, 600, 400)
    },
    {
      id: 3,
      icon: Trees,
      title: 'Agroindustria',
      description: 'Maquinaria y servicios para el procesamiento de productos agrícolas.',
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      services: [
        'Equipos de procesamiento agrícola',
        'Sistemas de secado y almacenamiento',
        'Maquinaria para exportación',
        'Tecnología de conservación'
      ],
      image: getOptimizedUrl(INDUSTRY_IMAGE_MAP.agri, 600, 400)
    },
    {
      id: 4,
      icon: Package,
      title: 'Industria Manufacturer',
      description: 'Soluciones integrales para plantas de manufactura y ensamblaje.',
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      services: [
        'Líneas de ensamblaje automatizadas',
        'Sistemas de control de calidad',
        'Equipos de fabricación industrial',
        'Mantenimiento predictivo'
      ],
      image: getOptimizedUrl(INDUSTRY_IMAGE_MAP.manufacturing, 600, 400)
    }
  ];

  const testimonials = [
    {
      name: 'Carlos Mendoza',
      company: 'Papelera del Pacífico',
      text: 'Atomic Corporation ha sido nuestro socio estratégico por más de 10 años. Su servicio técnico es excepcional.',
      rating: 5,
      image: getOptimizedUrl(ABOUT_IMAGE_MAP.carlosMendoza, 112, 112)
    },
    {
      name: 'Ana García',
      company: 'Procesadora Marina S.A.',
      text: 'La instalación de nuestros sistemas de refrigeración fue impecable. Cumplieron todos los plazos y calidad.',
      rating: 5,
      image: getOptimizedUrl(ABOUT_IMAGE_MAP.anaGarcia, 112, 112)
    },
    {
      name: 'Luis Rodríguez',
      company: 'Agroindustrias del Norte',
      text: 'Gracias a su asesoría técnica, optimizamos nuestra producción en un 30%. Excelentes profesionales.',
      rating: 5,
      image: getOptimizedUrl(ABOUT_IMAGE_MAP.luisRodriguez, 112, 112)
    }
  ];

  return (
    <section id="industries" className="section-padding bg-white">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-accent-100 text-accent-700 rounded-full text-sm font-medium mb-6">
            Industrias que Servimos
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Expertos en
            <span className="text-gradient"> Múltiples Sectores</span>
            <span className="text-gradient"> Industriales</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Contamos con experiencia comprobada en diversas industrias, 
            ofreciendo soluciones especializadas para cada sector.
          </p>
        </div>

        {/* Industry Tabs */}
        {isMobile ? (
          /* Mobile View - Expandable items */
          <div className="space-y-4">
            {industries.map((industry) => {
              const Icon = industry.icon;
              const isSelected = selectedIndustry === industry.id;
              
              return (
                <div key={industry.id} className="space-y-4">
                  <Card
                    className={`cursor-pointer transition-all duration-300 ${
                      isSelected
                        ? 'ring-2 ring-primary-500 shadow-lg'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedIndustry(isSelected ? -1 : industry.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 ${industry.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg font-bold text-gray-900">
                            {industry.title}
                          </CardTitle>
                          <CardDescription className="text-sm">
                            {industry.description}
                          </CardDescription>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                          isSelected ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Expanded Details for Mobile */}
                  {isSelected && (
                    <div className={`${industry.bgColor} rounded-2xl p-6 animate-in slide-in-from-top-2 duration-300`}>
                      <div className="space-y-4">
                        {/* Industry Image */}
                        <div className="relative h-48 rounded-xl overflow-hidden">
                          <img
                            src={industry.image}
                            alt={industry.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 ${industry.color} rounded-xl flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">
                              {industry.title}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {industry.description}
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-xl p-4">
                          <h4 className="font-bold text-gray-900 mb-3">Nuestros Servicios Especializados:</h4>
                          <ul className="space-y-2">
                            {industry.services.map((service, index) => (
                              <li key={index} className="flex items-center space-x-3">
                                <div className={`w-2 h-2 ${industry.color} rounded-full`}></div>
                                <span className="text-gray-700 text-sm">{service}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <Button className="w-full btn-primary" onClick={() => handleIndustryContact(industry.title)}>
                          Conocer Más sobre {industry.title}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop View - Side by side */
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Industry List */}
            <div className="space-y-4">
              {industries.map((industry) => {
                const Icon = industry.icon;
                return (
                  <Card
                    key={industry.id}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedIndustry === industry.id
                        ? 'ring-2 ring-primary-500 shadow-lg'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedIndustry(industry.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 ${industry.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg font-bold text-gray-900">
                            {industry.title}
                          </CardTitle>
                          <CardDescription className="text-sm">
                            {industry.description}
                          </CardDescription>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                          selectedIndustry === industry.id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>

            {/* Industry Details */}
            <div className={`${industries[selectedIndustry].bgColor} rounded-2xl p-8`}>
              <div className="space-y-6">
                {/* Industry Image */}
                <div className="relative h-64 rounded-xl overflow-hidden">
                  <img
                    src={industries[selectedIndustry].image}
                    alt={industries[selectedIndustry].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>

                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 ${industries[selectedIndustry].color} rounded-xl flex items-center justify-center`}>
                    {React.createElement(industries[selectedIndustry].icon, {
                      className: 'w-8 h-8 text-white'
                    })}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {industries[selectedIndustry].title}
                    </h3>
                    <p className="text-gray-600">
                      {industries[selectedIndustry].description}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-4">Nuestros Servicios Especializados:</h4>
                  <ul className="space-y-3">
                    {industries[selectedIndustry].services.map((service, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <div className={`w-2 h-2 ${industries[selectedIndustry].color} rounded-full`}></div>
                        <span className="text-gray-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button className="w-full btn-primary" onClick={() => handleIndustryContact(industries[selectedIndustry].title)}>
                  Conocer Más sobre {industries[selectedIndustry].title}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Testimonials */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Lo que dicen nuestros clientes
            </h3>
            <p className="text-gray-600 text-lg">
              Empresas líderes confían en nuestras soluciones industriales
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="text-center">
                <CardHeader>
                  {/* Imagen del cliente */}
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 mx-auto mb-4 overflow-hidden rounded-full shadow-lg">
                      <img
                        src={testimonial.image}
                        alt={`${testimonial.name} - ${testimonial.company}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  {/* Rating */}
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-700 italic">
                    "{testimonial.text}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.company}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-secondary-50 to-accent-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            ¿No encuentra su industria?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Contamos con capacidad para adaptarnos a cualquier requerimiento industrial. 
            Contáctenos y evaluemos sus necesidades específicas.
          </p>
          <Button className="btn-primary px-8" onClick={handleGeneralInquiry}>
            Consultar por su Industria
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Industries;
